import { Component } from '@theme/component';
import { TextMessageEvent, ValidationMessageEvent } from '@theme/events';

/**
 * @typedef {Object} ContactFormRefs
 * @property {HTMLFormElement} form
 * @property {HTMLTemplateElement} [errorIconTemplate]
 */

/**
 * @extends {Component<ContactFormRefs>}
 */
class ContactFormComponent extends Component {
  requiredRefs = ['form'];

  /** @type {AbortController | null} */
  #controller = null;
  /** @type {WeakMap<HTMLInputElement | HTMLTextAreaElement, HTMLSpanElement>} */
  #errorElements = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.refs.form.addEventListener('blur', this.#handleFieldBlur, { signal, capture: true });
    this.refs.form.addEventListener('submit', this.#handleFormSubmit, { signal });

    queueMicrotask(() => {
      const serverError = this.querySelector('.contact-form__error');
      if (!(serverError instanceof HTMLElement)) return;
      const message = (serverError.textContent || '').trim();
      if (!message) return;
      this.dispatchEvent(new TextMessageEvent(message));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * @param {Event} event
   */
  #handleFieldBlur = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
    if (field.type !== 'email') return;
    if (!field.value.trim()) {
      this.#clearFieldError(field);
      return;
    }

    const isValid = this.#validateField(field, { skipRequired: true });
    if (!isValid && field.validationMessage) {
      this.dispatchEvent(new ValidationMessageEvent(field.validationMessage, field.name || field.id || ''));
    }
  };

  /**
   * @param {SubmitEvent} event
   */
  #handleFormSubmit = (event) => {
    const { isValid, firstInvalidField } = this.#validateForm();

    if (!isValid) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (firstInvalidField?.validationMessage) {
        this.dispatchEvent(
          new ValidationMessageEvent(firstInvalidField.validationMessage, firstInvalidField.name || firstInvalidField.id || '')
        );
      }
      firstInvalidField?.focus();
    }
  };

  /**
   * Runs JS validation for all required fields and email format.
   * @returns {{ isValid: boolean, firstInvalidField: (HTMLInputElement | HTMLTextAreaElement | null) }}
   */
  #validateForm() {
    const requiredFields = this.#getRequiredFields();
    let isFormValid = true;
    /** @type {HTMLInputElement | HTMLTextAreaElement | null} */
    let firstInvalidField = null;

    for (const field of requiredFields) {
      const isFieldValid = this.#validateField(field);
      if (!isFieldValid) {
        isFormValid = false;
        if (!firstInvalidField) firstInvalidField = field;
      }
    }

    return { isValid: isFormValid, firstInvalidField };
  }

  /**
   * @returns {(HTMLInputElement | HTMLTextAreaElement)[]}
   */
  #getRequiredFields() {
    return Array.from(this.refs.form.querySelectorAll('input[required], textarea[required]')).filter(
      (field) => field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    );
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {{ skipRequired?: boolean }} [options]
   * @returns {boolean}
   */
  #validateField(field, options = {}) {
    this.#setCustomValidityMessage(field, options);
    const isValid = field.checkValidity();

    if (isValid) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
      const errorElement = this.#errorElements.get(field);
      if (errorElement) {
        this.#renderFieldError(errorElement, '');
      }
    } else {
      const errorElement = this.#getErrorElement(field);
      field.setAttribute('aria-invalid', 'true');
      this.#renderFieldError(errorElement, field.validationMessage);
      field.setAttribute('aria-describedby', errorElement.id);
    }

    return isValid;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   */
  #clearFieldError(field) {
    field.setCustomValidity('');
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
    const errorElement = this.#errorElements.get(field);
    if (errorElement) this.#renderFieldError(errorElement, '');
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {{ skipRequired?: boolean }} [options]
   */
  #setCustomValidityMessage(field, options = {}) {
    field.setCustomValidity('');

    if (!options.skipRequired && field.validity.valueMissing) {
      field.setCustomValidity(this.#requiredMessage);
      return;
    }

    if (field instanceof HTMLInputElement && field.type === 'email') {
      if (field.validity.typeMismatch || field.validity.patternMismatch) {
        field.setCustomValidity(this.#invalidEmailMessage);
      }
    }
  }

  get #requiredMessage() {
    return this.dataset.validationRequiredMessage || 'Please fill out this field.';
  }

  get #invalidEmailMessage() {
    return this.dataset.validationEmailMessage || 'Please enter a valid email address.';
  }

  /**
   * @param {Element | null} element
   * @param {string} message
   */
  #renderFieldError(element, message) {
    if (!(element instanceof HTMLElement)) return;
    const hasMessage = Boolean(message);
    element.hidden = !hasMessage;
    const messageElement = element.querySelector('.contact-form__field-error-message');
    if (!(messageElement instanceof HTMLElement)) return;
    messageElement.textContent = hasMessage ? message : '';
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @returns {HTMLSpanElement}
   */
  #getErrorElement(field) {
    const existing = this.#errorElements.get(field);
    if (existing) return existing;

    const errorElement = document.createElement('span');
    errorElement.id = `${field.id || field.name.replace(/[^a-zA-Z0-9_-]/g, '-')}-client-error`;
    errorElement.className = 'contact-form__field-error';
    errorElement.hidden = true;

    const icon = this.#createErrorIcon();
    if (icon) {
      errorElement.append(icon);
    }

    const messageElement = document.createElement('span');
    messageElement.className = 'contact-form__field-error-message';
    errorElement.append(messageElement);

    field.insertAdjacentElement('afterend', errorElement);

    this.#errorElements.set(field, errorElement);
    return errorElement;
  }

  #createErrorIcon() {
    const iconTemplate = this.refs.errorIconTemplate;
    if (!(iconTemplate instanceof HTMLTemplateElement)) return null;
    const iconNode = iconTemplate.content.firstElementChild?.cloneNode(true);
    if (!(iconNode instanceof SVGElement)) return null;
    return iconNode;
  }
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactFormComponent);
}
